import { Plan } from "@/lib/plans/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SYSTEM_CONFIG, SYSTEM_FIELD_CONFIG } from "@/lib/plans/enforce";
import { getSystemIcon } from "@/lib/system-icons";
import { formatCurrency } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { DefaultFeaturesSection } from "@/components/admin/DefaultFeaturesSection";

interface PublicSystemPlanCardProps {
  plan: Plan;
  systemCode: string;
  billingPeriod: 'monthly' | 'yearly';
  onSelect: (planId: string, billingPeriod: 'monthly' | 'yearly') => void;
  brandColors?: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

export function PublicSystemPlanCard({
  plan,
  systemCode,
  billingPeriod,
  onSelect,
  brandColors
}: PublicSystemPlanCardProps) {
  const config = SYSTEM_CONFIG[systemCode as keyof typeof SYSTEM_CONFIG];
  const IconComponent = getSystemIcon(systemCode);
  const systemConfig = SYSTEM_FIELD_CONFIG[systemCode as keyof typeof SYSTEM_FIELD_CONFIG];

  const primaryColor = brandColors?.primary || 'hsl(var(--primary))';
  const accentColor = brandColors?.accent || 'hsl(var(--accent))';

  // Filter limits to show only those relevant to this system
  const relevantLimits = systemConfig
    ? Object.entries(plan.features.limits || {})
        .filter(([key]) => (systemConfig.limits as readonly string[]).includes(key))
        .filter(([, value]) => value !== undefined && value !== null)
    : [];

  return (
    <Card className="relative group flex flex-col hover:shadow-card-hover transition-all duration-300 border-0 shadow-card">
      <CardHeader className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <IconComponent className="w-5 h-5" style={{ color: primaryColor }} />
          <h3 className="text-xl font-semibold">{plan.name}</h3>
        </div>
        
        {plan.description && (
          <p className="text-sm text-muted-foreground mb-2">{plan.description}</p>
        )}

        <div className="mt-4">
          <div className="flex items-center justify-center gap-2">
            <span className="text-3xl font-bold">{formatCurrency(plan.price_monthly)}</span>
            <span className="text-muted-foreground">/mês</span>
          </div>
          
          <div className="mt-2 text-sm text-muted-foreground">
            ou {formatCurrency(plan.price_yearly)}/ano
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">

        {relevantLimits.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium mb-2">Limites do Plano:</h4>
            <div className="space-y-2">
              {relevantLimits.map(([key, value]) => (
                <div key={key} className="flex items-center justify-between text-sm py-1">
                  <span className="text-muted-foreground">
                    {systemConfig?.labels[key] || key}
                  </span>
                  <span className="font-medium">
                    {value === 999999 ? (
                      <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                        Ilimitado
                      </Badge>
                    ) : (
                      (value as number).toLocaleString()
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Separator className="my-4" />

        {/* Recursos Padrão */}
        <div>
          <h4 className="font-medium mb-3 text-sm">Recursos Incluídos:</h4>
          <DefaultFeaturesSection variant="compact" />
        </div>
      </CardContent>

      {/* Footer com CTAs públicos */}
      <div className="flex flex-col gap-2 mt-auto p-6 pt-0">
        <Button
          onClick={() => onSelect(plan.id, 'monthly')}
          className="w-full text-white"
          style={{
            background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`
          }}
        >
          Assinar Mensal
        </Button>
        <Button
          onClick={() => onSelect(plan.id, 'yearly')}
          variant="outline"
          className="w-full"
          style={{
            borderColor: primaryColor,
            color: primaryColor
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = primaryColor;
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = primaryColor;
          }}
        >
          Assinar Anual (-15%)
        </Button>
      </div>
    </Card>
  );
}
